import { useEffect, useState } from "react";
import api from "../lib/api";
import { X, Save, Trash2, Sparkles, Paperclip } from "lucide-react";
import { toast } from "sonner";

export const CATEGORIES = { material: "Materiál", subdodavka: "Subdodávka", prace_vt: "Práce – vedoucí technik", sluzby: "Služby", rezie: "Režie", ostatni: "Ostatní" };

const inputCls = "w-full px-3 py-2 border border-slate-300 text-sm rounded-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-600";

import { InvoiceFuelings } from "./fleet/InvoiceFuelings";
import { InvoiceItemsEditor } from "./InvoiceItemsEditor";
import { InvoiceSplitEditor } from "./InvoiceSplitEditor";

export function ReceivedInvoiceModal({ editing, setEditing, jobs, onSaved, onDelete }) {
    const [suggest, setSuggest] = useState([]);
    useEffect(() => {
        if (editing.job_id) { setSuggest([]); return; }
        const text = [editing.supplier_name, editing.notes, editing.number, editing.ocr_text].filter(Boolean).join(" ");
        if (!text.trim() && !editing.issue_date) return;
        const t = setTimeout(() => api.get(`/received-invoices/suggest-job?text=${encodeURIComponent(text.slice(0, 500))}&date=${editing.issue_date || ""}`).then((r) => setSuggest(r.data.suggestions || [])).catch(() => {}), 400);
        return () => clearTimeout(t);
    }, [editing.supplier_name, editing.notes, editing.issue_date, editing.job_id, editing.number, editing.ocr_text]);
    const [saving, setSaving] = useState(false);
    const set = (k) => (e) => setEditing({ ...editing, [k]: e.target.value });
    const setNum = (k) => (e) => {
        const v = e.target.value;
        const next = { ...editing, [k]: v };
        const sub = Number(k === "subtotal" ? v : next.subtotal) || 0;
        const rate = Number(k === "vat_rate" ? v : next.vat_rate) || 0;
        if (k === "subtotal" || k === "vat_rate") {
            next.vat = Math.round(sub * rate) / 100;
            next.total = Math.round((sub + next.vat) * 100) / 100;
        } else if (k === "total") {
            const t = Number(v) || 0;
            next.subtotal = rate ? Math.round((t / (1 + rate / 100)) * 100) / 100 : t;
            next.vat = Math.round((t - next.subtotal) * 100) / 100;
        }
        setEditing(next);
    };

    const splitNotes = editing.delivery_notes || [];
    const splitSum = splitNotes.reduce((a, n) => a + (n.items || []).reduce((b, it) => b + Number(it.total || 0), 0), 0);
    const splitOk = splitNotes.length === 0 || !Number(editing.subtotal) || Math.abs(splitSum - Number(editing.subtotal)) <= 1;
    const save = async () => {
        if (!editing.supplier_name?.trim()) { toast.error("Vyplňte dodavatele"); return; }
        if (!splitOk) { toast.error("Součet dodacích listů nesedí se základem DPH faktury"); return; }
        if (!editing.due_date) { toast.error("Vyplňte datum splatnosti"); return; }
        const payload = {
            supplier_name: editing.supplier_name, supplier_ico: editing.supplier_ico || "", supplier_dic: editing.supplier_dic || "",
            number: editing.number || "", var_symbol: editing.var_symbol || "", bank_account: editing.bank_account || "",
            issue_date: editing.issue_date || "", due_date: editing.due_date,
            subtotal: Number(editing.subtotal) || 0, vat_rate: Number(editing.vat_rate) || 0, vat: Number(editing.vat) || 0, total: Number(editing.total) || 0,
            category: editing.category || "material", job_id: editing.job_id || null, budget_section: editing.budget_section || null, notes: editing.notes || "",
            status: editing.status || "neuhrazena", paid_at: editing.paid_at || null,
            items: (editing.items || []).filter((i) => i.name?.trim()).map((i) => ({ ...i, quantity: Number(i.quantity) || 1 })),
            delivery_notes: editing.delivery_notes === undefined ? null : editing.delivery_notes,
        };
        setSaving(true);
        try {
            let id = editing.id;
            if (id) await api.put(`/received-invoices/${id}`, payload);
            else { const { data } = await api.post("/received-invoices", payload); id = data.id; }
            if (editing._file) {
                const fd = new FormData();
                fd.append("file", editing._file);
                try { await api.post(`/received-invoices/${id}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
                catch { toast.warning("Faktura uložena, soubor se nepodařilo nahrát"); }
            }
            toast.success("Přijatá faktura uložena");
            setEditing(null);
            onSaved();
        } catch (e) { toast.error(e.response?.data?.detail || "Uložení selhalo"); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
            <div className="bg-white border-2 border-slate-900 max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="received-modal">
                <div className="flex items-center justify-between px-6 py-4 border-b-2 border-slate-900">
                    <h2 className="font-display text-lg font-bold">{editing.id ? "Upravit přijatou fakturu" : "Nová přijatá faktura"}</h2>
                    <button onClick={() => setEditing(null)}><X className="w-5 h-5 text-slate-400 hover:text-slate-900" /></button>
                </div>
                <div className="p-6 space-y-4">
                    {editing._scanned && (
                        <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-300 px-3 py-2 rounded-sm" data-testid="scanned-banner">
                            <Sparkles className="w-3.5 h-3.5" /> Údaje vytěžila AI z dokladu — zkontrolujte je prosím. Soubor se po uložení přiloží.
                        </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="md:col-span-2">
                            <label className="label-eyebrow block mb-1.5">Dodavatel *</label>
                            <input value={editing.supplier_name || ""} onChange={set("supplier_name")} data-testid="recv-supplier" className={inputCls} placeholder="Stavebniny DEK a.s." />
                        </div>
                        <div>
                            <label className="label-eyebrow block mb-1.5">Kategorie</label>
                            <select value={editing.category || "material"} onChange={set("category")} data-testid="recv-category" className={inputCls}>
                                {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Field label="IČO" v={editing.supplier_ico} on={set("supplier_ico")} testid="recv-ico" />
                        <Field label="DIČ" v={editing.supplier_dic} on={set("supplier_dic")} />
                        <Field label="Číslo faktury" v={editing.number} on={set("number")} testid="recv-number" />
                        <Field label="Variabilní symbol" v={editing.var_symbol} on={set("var_symbol")} testid="recv-vs" />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <Field label="Datum vystavení" type="date" v={editing.issue_date} on={set("issue_date")} />
                        <Field label="Splatnost *" type="date" v={editing.due_date} on={set("due_date")} testid="recv-due" />
                        <Field label="Účet dodavatele" v={editing.bank_account} on={set("bank_account")} placeholder="123456789/0100" />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-slate-50 border border-slate-200 p-3">
                        <Field label="Základ (bez DPH)" type="number" v={editing.subtotal} on={setNum("subtotal")} testid="recv-subtotal" />
                        <Field label="Sazba DPH %" type="number" v={editing.vat_rate} on={setNum("vat_rate")} testid="recv-vat-rate" />
                        <Field label="DPH" type="number" v={editing.vat} on={set("vat")} />
                        <Field label="Celkem k úhradě *" type="number" v={editing.total} on={setNum("total")} testid="recv-total" bold />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <label className="label-eyebrow block mb-1.5">Zakázka</label>
                            {splitNotes.length > 0 ? <div className="px-3 py-2 border border-sky-300 bg-sky-50 text-sm text-sky-900" data-testid="recv-job-split">{(() => { const c = Object.keys(splitNotes.reduce((a, n) => { n.items.forEach((it) => { a[it.target || n.target || "none"] = 1; }); return a; }, {})).length; return `Rozúčtováno po dodacích listech na ${c} ${c === 1 ? "cíl" : c < 5 ? "cíle" : "cílů"} – viz níže`; })()}</div> :
                            <select value={editing.job_id || ""} onChange={set("job_id")} data-testid="recv-job" className={inputCls}>
                                <option value="">— bez zakázky (režie) —</option>
                                {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}{j.client_name ? ` — ${j.client_name}` : ""}</option>)}
                            </select>}
                            {!editing.job_id && splitNotes.length === 0 && suggest.length > 0 && (
                                <div className="mt-1.5 text-[11px] space-y-1" data-testid="recv-job-suggestions">
                                    <div className="text-slate-500">Navržená zakázka (podle místa, data a popisu):</div>
                                    {suggest.slice(0, 3).map((j) => (
                                        <button key={j.id} onClick={() => setEditing({ ...editing, job_id: j.id })} className="w-full text-left px-2 py-1.5 border border-orange-300 bg-orange-50 hover:bg-orange-100 rounded-sm" data-testid={`recv-job-suggest-${j.id}`}>
                                            <b>{j.title}</b>{j.client_name ? ` — ${j.client_name}` : ""} <span className="text-slate-500">· {j.reason}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="label-eyebrow block mb-1.5">Stav</label>
                            <select value={editing.status || "neuhrazena"} onChange={set("status")} data-testid="recv-status" className={inputCls}>
                                <option value="neuhrazena">Neuhrazena</option>
                                <option value="uhrazena">Uhrazena</option>
                                <option value="stornovana">Stornována</option>
                            </select>
                        </div>
                    </div>
                    {editing.job_id && <SectionSelect jobId={editing.job_id} value={editing.budget_section || ""} onChange={set("budget_section")} />}
                    <Field label="Poznámka" v={editing.notes} on={set("notes")} testid="recv-notes" />
                    <div>
                        <label className="label-eyebrow block mb-1.5">Soubor dokladu (PDF / fotka)</label>
                        {editing.file_url && !editing._file && <div className="text-xs text-slate-600 mb-1 inline-flex items-center gap-1"><Paperclip className="w-3 h-3" /> {editing.file_name || "doklad"} — nahráním nového se nahradí</div>}
                        {editing._file && <div className="text-xs text-emerald-700 mb-1 inline-flex items-center gap-1"><Paperclip className="w-3 h-3" /> {editing._file.name} (přiloží se po uložení)</div>}
                        <input type="file" accept="image/*,application/pdf" onChange={(e) => setEditing({ ...editing, _file: e.target.files?.[0] })} data-testid="recv-file" className="w-full text-sm text-slate-600" />
                    </div>
                    <InvoiceSplitEditor notes={editing.delivery_notes || []} onChange={(delivery_notes) => setEditing({ ...editing, delivery_notes })} invoiceId={editing.id} hasFile={!!editing.file_url} jobs={jobs} subtotal={editing.subtotal} />
                    {splitNotes.length === 0 && <InvoiceItemsEditor items={editing.items || []} onChange={(items) => setEditing({ ...editing, items })} invoiceId={editing.id} hasFile={!!editing.file_url} />}
                    {editing.id && <InvoiceFuelings invoiceId={editing.id} />}
                </div>
                <div className="flex gap-2 px-6 py-4 bg-slate-50 border-t border-slate-200 justify-between">
                    <div>{editing.id && <button onClick={() => { onDelete(editing.id); setEditing(null); }} className="inline-flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-wider text-red-700 hover:bg-red-50 rounded-sm"><Trash2 className="w-3.5 h-3.5" /> Smazat</button>}</div>
                    <div className="flex gap-2">
                        <button onClick={() => setEditing(null)} className="px-4 py-2 text-xs uppercase tracking-wider border border-slate-300 hover:bg-slate-100 rounded-sm">Zrušit</button>
                        <button onClick={save} disabled={saving || !splitOk} data-testid="save-received" className="px-4 py-2 text-xs uppercase tracking-wider font-semibold bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white rounded-sm inline-flex items-center gap-1">
                            <Save className="w-3.5 h-3.5" /> {saving ? "Ukládám…" : "Uložit"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, v, on, type = "text", testid, placeholder, bold }) {
    return (
        <div>
            <label className="label-eyebrow block mb-1.5">{label}</label>
            <input type={type} value={v ?? ""} onChange={on} data-testid={testid} placeholder={placeholder} step={type === "number" ? "0.01" : undefined}
                   className={`${inputCls} ${bold ? "font-bold" : ""}`} />
        </div>
    );
}

// Oddíl rozpočtu zakázky (Rozpočet vs. skutečnost) – zobrazí se jen když má zakázka rozpočet s oddíly.
function SectionSelect({ jobId, value, onChange }) {
    const [sections, setSections] = useState([]);
    useEffect(() => { api.get(`/jobs/${jobId}/budget-sections`).then((r) => setSections(r.data.sections || [])).catch(() => setSections([])); }, [jobId]);
    if (!sections.length) return null;
    return (
        <div>
            <label className="label-eyebrow block mb-1.5">Oddíl rozpočtu (plán vs. skutečnost)</label>
            <select value={value} onChange={onChange} data-testid="recv-budget-section" className="w-full px-3 py-2 border border-slate-300 bg-white text-sm rounded-sm"><option value="">— nezařazeno —</option>{sections.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        </div>
    );
}
